import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth-context';
import type { CrewPlanInitialsSummary } from '@/lib/types';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

export function CrewInitialsCard({
  summary,
  timeZone,
  canInitial = false,
}: {
  summary?: CrewPlanInitialsSummary | null;
  timeZone: string;
  canInitial?: boolean;
}) {
  const { user } = useAuth();
  const [opened, setOpened] = useState(false);
  const [initials, setInitials] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [updatedSummary, setUpdatedSummary] = useState<CrewPlanInitialsSummary | null>(null);
  const planId = summary?.plan_revision_id;
  const planType = summary?.plan_type;
  const endpoint = planType === 'safe_work_plan' ? 'work-plans' : 'rescue-plans';

  useEffect(() => {
    if (!canInitial || !planId) return;
    let mounted = true;
    apiClient.post(`/api/v1/safety/${endpoint}/${planId}/open-revision/`)
      .then(() => { if (mounted) setOpened(true); })
      .catch(() => { if (mounted) setError('Unable to record that you opened this revision.'); });
    return () => { mounted = false; };
  }, [canInitial, endpoint, planId]);

  const current = updatedSummary?.plan_revision_id === planId ? updatedSummary : summary;
  if (!current) return null;
  // Only people with a linked crew identity can initial; guest rows carry a
  // null person_id and must never match a user without a Person.
  const personId = user?.person_id ?? null;
  const alreadyInitialled = personId !== null
    && current.initialled.some((entry) => entry.person_id === personId);
  const submitInitials = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await apiClient.post<{ crew_initials: CrewPlanInitialsSummary }>(
        `/api/v1/safety/${endpoint}/${planId}/initial/`,
        { initials },
      );
      setUpdatedSummary(response.data.crew_initials);
      setInitials('');
    } catch (err) {
      const data = isAxiosError<{ detail?: string; initials?: string | string[]; plan_initials?: string | string[] }>(err)
        ? err.response?.data
        : undefined;
      const serverReason = data?.detail ?? data?.initials ?? data?.plan_initials;
      setError(
        serverReason
          ? (Array.isArray(serverReason) ? serverReason.join(' ') : serverReason)
          : 'Unable to record your initials. Check that you opened this revision and try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Crew initials · Revision {current.version}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {current.initialled.length > 0 ? (
          <div className="space-y-2">
            {current.initialled.map((entry) => (
              <div
                key={`${entry.person_id ?? entry.name}-${entry.initialled_at}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
              >
                <div>
                  <p className="font-medium">{entry.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTimeInTimeZone(entry.initialled_at, timeZone)}
                  </p>
                </div>
                <Badge variant="outline">{entry.initials}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No crew initials collected yet.</p>
        )}

        {current.missing.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">
              Not yet initialled ({current.missing.length})
            </p>
            <p className="mt-1 text-sm text-amber-800">
              {current.missing.map((entry) => entry.name).join(', ')}
            </p>
          </div>
        )}
        {canInitial && personId !== null && !alreadyInitialled && (
          <div className="flex items-center gap-2">
            <Input aria-label="Your initials" value={initials} maxLength={20} placeholder="Your initials" onChange={(event) => setInitials(event.target.value)} className="max-w-40 uppercase" />
            <Button onClick={submitInitials} disabled={!opened || !initials.trim() || saving}>Initial this revision</Button>
          </div>
        )}
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
